import React, { useState, useMemo } from 'react';
import { DependencyNodeId } from 'sched';
import DependencyGraph from '../graph/DependencyGraph';
import DependenciesTable from './DependenciesTable';
import { DependenciesGanttChart } from '../charts/DependenciesGanttChart';
import { useDatabase } from '../../hooks/useDatabase';
import { useSchedule } from '../../hooks/useSchedule';

interface DependencyViewerProps {
  nodeId: DependencyNodeId;
  height?: string;
  className?: string;
}

const DependencyViewer: React.FC<DependencyViewerProps> = ({ 
  nodeId, 
  height = '400px',
  className = '' 
}) => {
  const [activeTab, setActiveTab] = useState('graph');
  const { dependencyGraph } = useDatabase();
  const { schedule } = useSchedule();
  
  // Get all related nodes (dependencies and dependents)
  const allNodeIds = useMemo(() => {
    if (!dependencyGraph) return [nodeId];
    
    const result = new Set<DependencyNodeId>();
    
    // Add the central node
    result.add(nodeId);
    
    // Add dependencies (what this node depends on)
    const dependencies = dependencyGraph.get_dependencies(nodeId);
    dependencies.forEach(depId => result.add(depId));
    
    // Add dependents (what depends on this node)
    const dependents = dependencyGraph.get_dependents(nodeId);
    dependents.forEach(depId => result.add(depId));
    
    return Array.from(result);
  }, [nodeId, dependencyGraph]);

  // Determine if there is anything to show in the Gantt tab
  const hasScheduledItems = useMemo(() => {
    if (!dependencyGraph || !schedule) return true; // avoid flicker while loading
    // The fuzzy schedule DTO has `schedule.items` keyed by node ID
    const items = (schedule as any)?.schedule?.items ?? {};
    return Object.keys(items).length > 0;
  }, [dependencyGraph, schedule, nodeId]);
  
  return (
    <div className={`${className} flex flex-col`} style={{ height }}>
      {/* Tabs */}
      <div className="border-b border-cm-border-subtle mb-4 flex-shrink-0">
        <ul className="flex -mb-px text-sm font-medium text-center text-cm-text-muted w-full">
          <li className="flex-1">
            <button
              className={`w-full inline-flex items-center justify-center p-4 border-b-2 ${activeTab === 'graph' ? 'text-blue-600 border-blue-600 dark:border-blue-500' : 'border-transparent hover:text-cm-text-secondary hover:border-cm-border-subtle'}`}
              onClick={() => setActiveTab('graph')}
            >
              Graph View
            </button>
          </li>
          <li className="flex-1">
            <button
              className={`w-full inline-flex items-center justify-center p-4 border-b-2 ${activeTab === 'table' ? 'text-blue-600 border-blue-600 dark:border-blue-500' : 'border-transparent hover:text-cm-text-secondary hover:border-cm-border-subtle'}`}
              onClick={() => setActiveTab('table')}
            >
              Table View
            </button>
          </li>
          <li className="flex-1">
            <button
              className={`w-full inline-flex items-center justify-center p-4 border-b-2 ${activeTab === 'gantt' ? 'text-blue-600 border-blue-600 dark:border-blue-500' : 'border-transparent hover:text-cm-text-secondary hover:border-cm-border-subtle'}`}
              onClick={() => setActiveTab('gantt')}
            >
              Gantt Chart
            </button>
          </li>
        </ul>
      </div>

      {/* Content area - using flex-grow to fill remaining space */}
      <div className="flex-grow overflow-hidden">
        {activeTab === 'graph' && (
          <div className="h-full">
            <DependencyGraph 
              nodeIds={allNodeIds} 
              className="h-full" 
            />
          </div>
        )}
        {activeTab === 'table' && (
          <div className="h-full overflow-auto">
            <DependenciesTable 
              nodeId={nodeId} 
              className="h-full" 
            />
          </div>
        )}
        {activeTab === 'gantt' && (
          <div className="h-full">
            {hasScheduledItems ? (
              <DependenciesGanttChart
                dependencyNodeId={nodeId}
                title="Schedule Timeline"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-cm-text-muted">
                <div className="text-center">
                  <div className="font-medium mb-1">No scheduled dependency items to display</div>
                  <div className="opacity-80">Use Work Matching or set availability to generate a schedule.</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DependencyViewer;
